import {
    CalendarIcon,
    PhoneCallIcon,
    EmailIcon,
    CommentIcon,
    CogIcon,
    VoicemailIcon,
    AudienceIcon,
    AlarmClockIcon,
    CompanyTypeIcon,
    LightbulbBrightIcon,
    VirtualMeetingIcon,
    StarIcon,
    Pencil,
    MeetingIcon,
    SidebarIconNotificationsIcon,
    BookIcon,
    HeartIcon,
    HomeIcon,
    FlagIcon,
    TrophyIcon,
} from 'shells/icon';

import type {ActivityTypeIcon} from 'nutshell-graphql-types';

// Numeric values should line up with those in Application/Models/Activity/Type.php
export function getIconForActivityType(
    activityTypeIcon: ActivityTypeIcon | number
): React.ComponentType<any> {
    switch (activityTypeIcon) {
        case 19:
        case 'HANDSHAKE':
            return MeetingIcon;
        case 18:
        case 'GEAR':
            return CogIcon;
        case 17:
        case 'LIGHTBULB':
            return LightbulbBrightIcon;
        case 16:
        case 'BOOK':
            return BookIcon;
        case 15:
        case 'TROPHY':
            return TrophyIcon;
        case 14:
        case 'FLAG':
            return FlagIcon;
        case 13:
        case 'HEART':
            return HeartIcon;
        case 12:
        case 'BELL':
            return SidebarIconNotificationsIcon;
        case 11:
        case 'ALARM':
            return AlarmClockIcon;
        case 10:
        case 'STAR':
            return StarIcon;
        case 9:
        case 'BRIEFCASE':
            return CompanyTypeIcon;
        case 8:
        case 'PEOPLE':
            return AudienceIcon;
        case 7:
        case 'VIDEO':
            return VirtualMeetingIcon;
        case 6:
        case 'PENCIL':
            return Pencil;
        case 5:
        case 'VOICEMAIL':
            return VoicemailIcon;
        case 4:
        case 'CHAT':
            return CommentIcon;
        case 3:
        case 'HOME':
            return HomeIcon;
        case 2:
        case 'PHONE':
            return PhoneCallIcon;
        case 1:
        case 'EMAIL':
            return EmailIcon;
        default:
            return CalendarIcon;
    }
}

// Up to this point we've been using the id of the activity type instead of the api id for our setting value.
// This worked since we were rendering our page in PHTML and we were using a custom select component.
// Now that we're using React and a select component that expects api ids, we need to convert any
// ids to api ids.
export function getDefaultActivityTypeSettingValue(value: string): string {
    const apiIdPattern = /^\d+-activityTypes$/;
    if (apiIdPattern.test(value)) {
        return value;
    }

    return `${value}-activityTypes`;
}

/**
 * Suggests an appropriate icon for an activity type based on its name
 * @param name - The activity type name to analyze
 * @returns The suggested ActivityTypeIcon
 */
export function suggestIconForActivityType(name: string): ActivityTypeIcon {
    if (!name) {
        // @ts-expect-error
        return 'DEFAULT';
    }

    const lowerName = name.toLowerCase();

    // Video/Virtual meetings
    if (
        lowerName.includes('zoom') ||
        lowerName.includes('video') ||
        lowerName.includes('virtual') ||
        lowerName.includes('webinar') ||
        lowerName.includes('online meeting') ||
        lowerName.includes('teams') ||
        lowerName.includes('google meet') ||
        lowerName.includes('skype')
    ) {
        // @ts-expect-error
        return 'VIDEO';
    }

    // Phone calls
    if (
        lowerName.includes('phone') ||
        lowerName.includes('call') ||
        lowerName.includes('dial') ||
        lowerName.includes('telephone')
    ) {
        // @ts-expect-error
        return 'PHONE';
    }

    // Voicemails
    if (
        lowerName.includes('voicemail') ||
        lowerName.includes('vm') ||
        lowerName.includes('voice mail')
    ) {
        // @ts-expect-error
        return 'VOICEMAIL';
    }

    // Meetings
    if (
        lowerName.includes('meeting') ||
        lowerName.includes('conference') ||
        lowerName.includes('appointment') ||
        lowerName.includes('session') ||
        lowerName.includes('consultation') ||
        lowerName.includes('interview')
    ) {
        // @ts-expect-error
        return 'HANDSHAKE';
    }

    // Email
    if (lowerName.includes('email') || lowerName.includes('mail') || lowerName.includes('e-mail')) {
        // @ts-expect-error
        return 'EMAIL';
    }

    // Notes and documentation
    if (
        lowerName.includes('note') ||
        lowerName.includes('memo') ||
        lowerName.includes('comment') ||
        lowerName.includes('documentation') ||
        lowerName.includes('summary')
    ) {
        // @ts-expect-error
        return 'PENCIL';
    }

    // Chat and messaging
    if (
        lowerName.includes('chat') ||
        lowerName.includes('message') ||
        lowerName.includes('text') ||
        lowerName.includes('sms') ||
        lowerName.includes('instant message')
    ) {
        // @ts-expect-error
        return 'CHAT';
    }

    // Presentations and demos
    if (
        lowerName.includes('demo') ||
        lowerName.includes('presentation') ||
        lowerName.includes('pitch') ||
        lowerName.includes('showcase') ||
        lowerName.includes('walkthrough')
    ) {
        // @ts-expect-error
        return 'LIGHTBULB';
    }

    // Training and education
    if (
        lowerName.includes('training') ||
        lowerName.includes('education') ||
        lowerName.includes('workshop') ||
        lowerName.includes('seminar') ||
        lowerName.includes('tutorial') ||
        lowerName.includes('course')
    ) {
        // @ts-expect-error
        return 'BOOK';
    }

    // Reminders and follow-ups
    if (
        lowerName.includes('reminder') ||
        lowerName.includes('follow-up') ||
        lowerName.includes('followup') ||
        lowerName.includes('check-in') ||
        lowerName.includes('checkin') ||
        lowerName.includes('alarm')
    ) {
        // @ts-expect-error
        return 'ALARM';
    }

    // Tasks and to-dos
    if (
        lowerName.includes('task') ||
        lowerName.includes('to-do') ||
        lowerName.includes('todo') ||
        lowerName.includes('action item') ||
        lowerName.includes('assignment')
    ) {
        // @ts-expect-error
        return 'FLAG';
    }

    // Achievements and successes
    if (
        lowerName.includes('achievement') ||
        lowerName.includes('win') ||
        lowerName.includes('success') ||
        lowerName.includes('victory') ||
        lowerName.includes('milestone') ||
        lowerName.includes('goal')
    ) {
        // @ts-expect-error
        return 'TROPHY';
    }

    // Team and group activities
    if (
        lowerName.includes('team') ||
        lowerName.includes('group') ||
        lowerName.includes('collaboration') ||
        lowerName.includes('brainstorm') ||
        lowerName.includes('standup') ||
        lowerName.includes('huddle')
    ) {
        // @ts-expect-error
        return 'PEOPLE';
    }

    // Notifications and alerts
    if (
        lowerName.includes('notification') ||
        lowerName.includes('alert') ||
        lowerName.includes('ping') ||
        lowerName.includes('announcement')
    ) {
        // @ts-expect-error
        return 'BELL';
    }

    // Business and professional
    if (
        lowerName.includes('business') ||
        lowerName.includes('corporate') ||
        lowerName.includes('professional') ||
        lowerName.includes('formal') ||
        lowerName.includes('contract') ||
        lowerName.includes('proposal')
    ) {
        // @ts-expect-error
        return 'BRIEFCASE';
    }

    // Special occasions
    if (
        lowerName.includes('celebration') ||
        lowerName.includes('anniversary') ||
        lowerName.includes('party') ||
        lowerName.includes('special') ||
        lowerName.includes('birthday')
    ) {
        // @ts-expect-error
        return 'HEART';
    }

    // Home or personal
    if (
        lowerName.includes('home') ||
        lowerName.includes('personal') ||
        lowerName.includes('family') ||
        lowerName.includes('private')
    ) {
        // @ts-expect-error
        return 'HOME';
    }

    // Important or high priority
    if (
        lowerName.includes('important') ||
        lowerName.includes('priority') ||
        lowerName.includes('urgent') ||
        lowerName.includes('critical') ||
        lowerName.includes('star') ||
        lowerName.includes('vip')
    ) {
        // @ts-expect-error
        return 'STAR';
    }

    // Configuration or settings
    if (
        lowerName.includes('config') ||
        lowerName.includes('setting') ||
        lowerName.includes('setup') ||
        lowerName.includes('admin') ||
        lowerName.includes('maintenance')
    ) {
        // @ts-expect-error
        return 'GEAR';
    }

    // Default fallback
    // @ts-expect-error
    return 'DEFAULT';
}
